import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, MessageCircle, Mail, Lock, Unlock, Trash2, KeyRound, MoreVertical } from "lucide-react";
import { PasswordResetDialog } from "@/components/PasswordResetDialog";
import { CustomerDetailsDialog } from "@/components/CustomerDetailsDialog";
import { IdCard } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { StatusPill } from "@/components/VenueBits";
import { ReasonDialog } from "@/components/BookingStatus";
import { AccountDeleteDialog, RegKind, RegRow } from "@/components/AccountDeleteDialog";
import { Input, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { fmtDate } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";
import { useLive } from "@/lib/live";

const waNum = (p: string) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("07")) d = "964" + d.slice(1);
  return d;
};

const RowCard = ({ r, kind, isSuper, onToggle, onDelete, onReset, onDetails }: { r: RegRow; kind: RegKind; isSuper: boolean; onToggle: () => void; onDelete: () => void; onReset: () => void; onDetails: () => void }) => {
  const { t, lang } = usePrefs();
  const title = kind === "partners" ? r.business_name : r.name;
  const sub = r.username || r.email || "";
  const item = "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer rounded-lg";
  return (
    <div data-testid={`reg-row-${r.id}`} className={`rounded-2xl border bg-card p-3 flex items-center gap-3 min-w-0 ${r.disabled ? "border-dashed opacity-80" : ""}`}>
      <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center font-display text-lg font-black">{(r.business_name || r.name || "?").trim().charAt(0)}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="font-bold truncate" data-testid={`reg-name-${r.id}`}>{title}</p>
          {r.disabled ? <span data-testid={`reg-disabled-${r.id}`} className="shrink-0 rounded-full bg-zinc-700 text-white px-2 py-0.5 text-[10px] font-bold">{t("reg.disabledBadge")}</span>
            : kind === "partners" && r.status && r.status !== "approved" && <StatusPill status={r.status} />}
        </div>
        {sub && <p className="text-xs text-muted-foreground truncate" data-testid={`reg-username-${r.id}`}><bdi dir="ltr">{sub}</bdi></p>}
        <p className="text-[11px] text-muted-foreground truncate"><bdi dir="ltr">{r.phone || t("reg.noPhone")}</bdi></p>
      </div>
      {r.phone && <a href={`https://wa.me/${waNum(r.phone)}?text=${encodeURIComponent(t("reg.waMsg").replace("{name}", r.name || ""))}`} target="_blank" rel="noreferrer" aria-label={t("reg.wa")}
        data-testid={`reg-whatsapp-${r.id}`} className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-[#25D366] text-white hover:bg-[#1ebe5b]"><MessageCircle className="h-4 w-4" /></a>}
      {isSuper && (
        <DropdownMenu dir={lang === "ar" ? "rtl" : "ltr"}>
          <DropdownMenuTrigger asChild>
            <button aria-label={t("reg.more")} data-testid={`reg-more-${r.id}`} className="h-9 w-9 shrink-0 grid place-items-center rounded-full border hover:bg-accent"><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[11rem] rounded-xl p-1">
            {r.email && <div className="px-3 py-1 text-xs text-muted-foreground truncate" dir="ltr"><Mail className="inline h-3 w-3 me-1" />{r.email}</div>}
            {r.created_at && <div className="px-3 pb-1.5 text-xs text-muted-foreground" data-testid={`reg-joined-${r.id}`}>{t("reg.joined").replace("{d}", fmtDate(r.created_at, lang))}</div>}
            {kind === "customers" && <DropdownMenuItem onSelect={onDetails} data-testid={`reg-edit-details-${r.id}`} className={item}><IdCard className="h-4 w-4" />{t("reg.editDetails")}</DropdownMenuItem>}
            {<DropdownMenuItem onSelect={onReset} data-testid={`reg-reset-password-${r.id}`} className={item}><KeyRound className="h-4 w-4" />{t("reg.resetPw")}</DropdownMenuItem>}
            <DropdownMenuItem onSelect={onToggle} data-testid={`reg-toggle-${r.id}`} className={item}>{r.disabled ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}{t(r.disabled ? "reg.enable" : "reg.disable")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={onDelete} data-testid={`reg-delete-${r.id}`} className={`${item} text-primary`}><Trash2 className="h-4 w-4" />{t("reg.delete")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

export default function AdminAccounts() {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const isSuper = (me.admin_role || "super") === "super";
  const [kind, setKind] = useState<RegKind>("partners");
  const [q, setQ] = useState("");
  const [list, setList] = useState<RegRow[] | null>(null);
  const [disabling, setDisabling] = useState<RegRow | null>(null);
  const [deleting, setDeleting] = useState<RegRow | null>(null);
  const [resetting, setResetting] = useState<RegRow | null>(null);
  const [editingDetails, setEditingDetails] = useState<RegRow | null>(null);
  const load = () => api.get(`/admin/registry/${kind}`, { params: { q } }).then((r) => setList(r.data)).catch(() => setList([]));
  useLive(() => load());
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [kind, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (r: RegRow, reason = "") => {
    try {
      await api.post(`/admin/registry/${kind}/${r.id}/access/${r.disabled ? "enable" : "disable"}`, { reason });
      toast.success(t(r.disabled ? "reg.enabled" : "reg.disabled"));
      setDisabling(null);
    } catch (e) {
      toast.error(errorText(e, t));
    }
    load();
  };
  return (
    <div data-testid="admin-accounts-page">
      <PageTitle title={t("reg.title")} sub={t("reg.sub")} />
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {(["partners", "customers"] as const).map((k) => (
          <button key={k} onClick={() => { setKind(k); setList(null); }} aria-pressed={kind === k} data-testid={`reg-tab-${k}`}
            className={`h-10 px-5 rounded-full text-sm font-bold transition-[background-color,color] ${kind === k ? "bg-foreground text-background" : "bg-card border hover:bg-accent"}`}>{t(`reg.${k}`)}</button>
        ))}
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="h-4 w-4 absolute top-3 start-3 text-muted-foreground" />
          <Input value={q} onChange={(e: any) => setQ(e.target.value)} placeholder={t("reg.search")} data-testid="reg-search" className="h-10 rounded-full ps-9" />
        </div>
      </div>
      {list?.length === 0 && <p data-testid="reg-empty" className="text-muted-foreground py-10 text-center">{t("reg.empty")}</p>}
      <div className="grid gap-2 lg:grid-cols-2">
        {list?.map((r) => <RowCard key={r.id} r={r} kind={kind} isSuper={isSuper} onToggle={() => (r.disabled ? toggle(r) : setDisabling(r))} onDelete={() => setDeleting(r)} onReset={() => setResetting(r)} onDetails={() => setEditingDetails(r)} />)}
      </div>
      {disabling && <ReasonDialog testid="reg-disable-dialog" title={t("reg.disable")} note={t(kind === "partners" ? "reg.disableNotePartner" : "reg.disableNoteCustomer")}
        onClose={() => setDisabling(null)} onSubmit={(reason) => toggle(disabling, reason)} />}
      {editingDetails && <CustomerDetailsDialog userId={editingDetails.id} name={editingDetails.name} phone={editingDetails.phone} onClose={() => setEditingDetails(null)} onDone={() => { setEditingDetails(null); load(); }} />}
      {resetting && <PasswordResetDialog accountId={resetting.id} phone={resetting.phone} customerId={kind === "customers" ? resetting.id : undefined} onClose={() => setResetting(null)} />}
      {deleting && <AccountDeleteDialog kind={kind} row={deleting} onClose={() => { setDeleting(null); load(); }} onDone={() => { setDeleting(null); load(); }} />}
    </div>
  );
}
